import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Divider,
  TextField,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Pie, Bar } from "react-chartjs-2";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../../utils/api";

function isTextQuestion(t) {
  const x = String(t || "").toLowerCase();
  return x === "text" || x === "open" || x === "textbox" || x === "free_text";
}

function pickChartKind(q) {
  const optCount = q?.options?.length || 0;
  const type = String(q?.question_type || "").toLowerCase();

  if (optCount <= 0) return "none";
  if (type === "yesno" || type === "yes-no" || type === "boolean") return "pie";
  if (optCount <= 6) return "pie";
  return "bar";
}

const PALETTE = [
  "#3B82F6",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#A855F7",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
  "#64748B",
  "#EC4899",
];

const withAlpha = (hex, alpha = 0.25) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const ResultsTab = ({ election }) => {
  const [questions, setQuestions] = useState([]);
  const [results, setResults] = useState(null);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isCompliance, setIsCompliance] = useState(false);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [viewRow, setViewRow] = useState(null);
  const [drill, setDrill] = useState(null);

  const pdfRef = useRef(null);

  useEffect(() => {
    if (!election?.id) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [election?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [qRes, rRes, sRes, dRes] = await Promise.all([
        api.get(`/elections/${election.id}/questions`),
        api.get(`/elections/${election.id}/results`),
        api.get(`/elections/${election.id}/stats`),
        api.get(
          `/elections/${election.id}/results/detailed?search=${encodeURIComponent("")}`
        ),
      ]);

      setQuestions(qRes.data || []);
      setResults(rRes.data || null);
      setStats(sRes.data || null);
      setSubmissions(dRes.data?.submissions || []);
      setIsCompliance(!!dRes.data?.isCompliance);
    } catch (err) {
      console.error("Failed loading results:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailed = async () => {
    try {
      const dRes = await api.get(
        `/elections/${election.id}/results/detailed?search=${encodeURIComponent(search)}`
      );
      setSubmissions(dRes.data?.submissions || []);
      setIsCompliance(!!dRes.data?.isCompliance);
    } catch (err) {
      console.error("Failed loading detailed results:", err);
    }
  };

  const participationRate = useMemo(() => {
    if (!stats?.invited || stats.invited === 0) return 0;
    return Math.round(((stats.voted || 0) / stats.invited) * 1000) / 10;
  }, [stats]);

  const submissionsSafe = submissions || [];

  const exportSubmissionsCSV = () => {
    const headers = [
      "VoteId",
      "FullName",
      "Email",
      "MemberCode",
      "SubmittedAt",
      "IpAddress",
      "InviteType",
    ];

    const rows = submissionsSafe.map((r) => [
      r.VoteId ?? "",
      r.FullName ?? "",
      r.UserEmail ?? r.InviteEmail ?? "",
      r.MemberCode ?? "",
      r.SubmittedAt ? new Date(r.SubmittedAt).toISOString() : "",
      r.IpAddress ?? "",
      r.InviteType ?? "",
    ]);

    const csv = [headers, ...rows]
      .map((line) =>
        line.map((x) => `"${String(x).replaceAll('"', '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Election_${election.id}_Submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!pdfRef.current) return;
    const canvas = await html2canvas(pdfRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const width = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const height = (canvas.height * width) / canvas.width;

    if (height <= pageHeight) {
      pdf.addImage(imgData, "PNG", 0, 0, width, height);
    } else {
      let heightLeft = height;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, width, height);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - height;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, width, height);
        heightLeft -= pageHeight;
      }
    }

    pdf.save(`Election_${election.id}_Results.pdf`);
  };

  const openDrillDownForOption = ({
    questionId,
    questionText,
    optionId,
    optionText,
  }) => {
    const rows = submissionsSafe.filter((s) => {
      const answers = s.answers || [];
      return answers.some((a) => {
        if (Number(a.QuestionId) !== Number(questionId)) return false;
        return (
          Number(a.OptionId) === Number(optionId) ||
          Number(a.MultiOptionId) === Number(optionId)
        );
      });
    });

    setDrill({ questionId, questionText, optionId, optionText, rows });
  };

  const handlePieClick = (_evt, items, q, labels, optionIds) => {
    if (!items?.length) return;
    const idx = items[0].index;
    const optionId = optionIds[idx];
    const optionText = labels[idx];
    if (!optionId) return;

    openDrillDownForOption({
      questionId: q.id,
      questionText: q.question_text,
      optionId,
      optionText,
    });
  };

  const handleBarClick = (_evt, items, q, labels, optionIds) => {
    if (!items?.length) return;
    const idx = items[0].index;
    const optionId = optionIds[idx];
    const optionText = labels[idx];
    if (!optionId) return;

    openDrillDownForOption({
      questionId: q.id,
      questionText: q.question_text,
      optionId,
      optionText,
    });
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: 4,
          textAlign: "center",
        }}
      >
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Box sx={{ width: "100%" }} ref={pdfRef}>
      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Results
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              {election?.title || `Election ${election?.id}`}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={exportSubmissionsCSV}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Detailed Export (CSV)
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={exportPDF}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Export PDF
            </Button>

            <Tooltip title="Refresh">
              <IconButton
                onClick={loadAll}
                sx={{
                  borderRadius: "10px",
                  border: "1px solid rgba(47,99,173,0.12)",
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}
        >
          <Chip
            label={`Total votes: ${results?.totalVotes ?? 0}`}
            sx={{
              fontWeight: 700,
              color: "#2f63ad",
              backgroundColor: "rgba(47,99,173,0.08)",
            }}
          />
          <Chip label={`Invited: ${stats?.invited ?? 0}`} />
          <Chip
            label={`Opened: ${stats?.opened ?? 0}`}
            sx={{
              fontWeight: 700,
              color: "#b45309",
              backgroundColor: "rgba(245,158,11,0.12)",
            }}
          />
          <Chip
            label={`Voted: ${stats?.voted ?? 0}`}
            sx={{
              fontWeight: 700,
              color: "#15803d",
              backgroundColor: "rgba(34,197,94,0.12)",
            }}
          />
          <Chip label={`Participation: ${participationRate}%`} />
        </Stack>
      </Card>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 2.5 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Search Submissions
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Search detailed submission results by keyword.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search keyword"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadDetailed()}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
            <Button
              variant="contained"
              onClick={loadDetailed}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#275592",
                  boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                },
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Card>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 2, md: 3 },
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          Questions Overview
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Click chart segments to see who selected each option.
        </Typography>

        <Divider sx={{ mb: 2.5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {questions.map((q) => {
            const raw = results?.results?.[q.id] || {
              optionCounts: {},
              textAnswers: [],
            };

            if (isTextQuestion(q.question_type)) {
              return (
                <Card
                  key={q.id}
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
                    backgroundColor: "rgba(255,255,255,0.98)",
                  }}
                >
                  <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                      {q.question_text}
                    </Typography>

                    <Chip
                      label={`Text responses: ${raw.textAnswers?.length || 0}`}
                      sx={{ mt: 1.5, mb: 2 }}
                    />

                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                      {(raw.textAnswers || []).length === 0 ? (
                        <Typography sx={{ color: "#6b778c" }}>
                          No responses
                        </Typography>
                      ) : (
                        (raw.textAnswers || []).map((t, i) => (
                          <TextField
                            key={i}
                            fullWidth
                            value={t}
                            sx={{
                              mb: 1,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                              },
                            }}
                            InputProps={{ readOnly: true }}
                          />
                        ))
                      )}
                    </Box>
                  </Box>
                </Card>
              );
            }

            const options = q.options || [];
            if (options.length === 0) {
              return (
                <Card
                  key={q.id}
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
                    backgroundColor: "rgba(255,255,255,0.98)",
                  }}
                >
                  <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                      {q.question_text}
                    </Typography>
                    <Typography sx={{ color: "#6b778c", mt: 1.5 }}>
                      No options found for this question.
                    </Typography>
                  </Box>
                </Card>
              );
            }

            const labels = options.map((o) => o.option_text || "Untitled");
            const optionIds = options.map((o) => o.id);
            const counts = options.map((o) => raw.optionCounts?.[o.id] || 0);
            const total = counts.reduce((a, b) => a + b, 0);

            const kind = pickChartKind(q);
            const colors = labels.map((_, i) => PALETTE[i % PALETTE.length]);
            const softColors = colors.map((c) => withAlpha(c, 0.25));

            const data =
              kind === "pie"
                ? {
                    labels,
                    datasets: [
                      {
                        label: "Votes",
                        data: counts,
                        backgroundColor: softColors,
                        borderColor: colors,
                        borderWidth: 2,
                      },
                    ],
                  }
                : {
                    labels,
                    datasets: [
                      {
                        label: "Votes",
                        data: counts,
                        backgroundColor: softColors,
                        borderColor: colors,
                        borderWidth: 2,
                        borderRadius: 8,
                      },
                    ],
                  };

            return (
              <Card
                key={q.id}
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
                  backgroundColor: "rgba(255,255,255,0.98)",
                }}
              >
                <Box sx={{ p: 2.5 }}>
                  <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                    {q.question_text}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                    Total responses: {total}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ height: 260 }}>
                    {kind === "pie" ? (
                      <Pie
                        data={data}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "right",
                              labels: { boxWidth: 12, boxHeight: 12 },
                            },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => {
                                  const v = ctx.parsed;
                                  const pct = total
                                    ? Math.round((v / total) * 1000) / 10
                                    : 0;
                                  return `${ctx.label}: ${v} (${pct}%)`;
                                },
                              },
                            },
                          },
                        }}
                        onClick={(evt, items) =>
                          handlePieClick(evt, items, q, labels, optionIds)
                        }
                      />
                    ) : (
                      <Bar
                        data={data}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => {
                                  const v = ctx.parsed.y;
                                  const pct = total
                                    ? Math.round((v / total) * 1000) / 10
                                    : 0;
                                  return `Votes: ${v} (${pct}%)`;
                                },
                              },
                            },
                          },
                          scales: {
                            y: { beginAtZero: true, ticks: { stepSize: 1 } },
                            x: { ticks: { maxRotation: 0, autoSkip: true } },
                          },
                        }}
                        onClick={(evt, items) =>
                          handleBarClick(evt, items, q, labels, optionIds)
                        }
                      />
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {options.map((opt, i) => {
                    const count = raw.optionCounts?.[opt.id] || 0;
                    return (
                      <Box
                        key={opt.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.9,
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: colors[i % colors.length],
                              flex: "0 0 auto",
                            }}
                          />
                          <Typography
                            variant="body2"
                            noWrap
                            title={opt.option_text}
                            sx={{ color: "#223142" }}
                          >
                            {opt.option_text}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexShrink: 0,
                          }}
                        >
                          <Chip size="small" label={`${count} votes`} />
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: "10px",
                            }}
                            onClick={() =>
                              openDrillDownForOption({
                                questionId: q.id,
                                questionText: q.question_text,
                                optionId: opt.id,
                                optionText: opt.option_text,
                              })
                            }
                          >
                            View voters
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Card>
            );
          })}
        </Box>
      </Card>

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: 2 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
          >
            Submissions
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review submitted votes and open a full submission record.
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{
              minWidth: 860,
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
                fontWeight: 700,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "rgba(47,99,173,0.025)",
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                borderBottom: "1px solid rgba(47,99,173,0.06)",
                color: "#223142",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Full Name</TableCell>
                {!isCompliance && <TableCell>Email</TableCell>}
                <TableCell>Date Submitted</TableCell>
                <TableCell>Member Code</TableCell>
                {!isCompliance && <TableCell>IP Address</TableCell>}
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {submissionsSafe.map((r, idx) => (
                <TableRow key={r.VoteId || idx} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{r.FullName || "N/A"}</TableCell>
                  {!isCompliance && <TableCell>{r.UserEmail || r.InviteEmail || "N/A"}</TableCell>}
                  <TableCell>
                    {r.SubmittedAt
                      ? new Date(r.SubmittedAt).toLocaleString()
                      : "N/A"}
                  </TableCell>
                  <TableCell>{r.MemberCode || "N/A"}</TableCell>
                  {!isCompliance && <TableCell>{r.IpAddress || "N/A"}</TableCell>}
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setViewRow(r)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        borderColor: "rgba(47,99,173,0.18)",
                        color: "#2f63ad",
                        "&:hover": {
                          borderColor: "#2f63ad",
                          backgroundColor: "rgba(47,99,173,0.04)",
                        },
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

              {submissionsSafe.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 4, textAlign: "center" }}>
                      <Typography sx={{ color: "#6b778c" }}>
                        No submissions found.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Dialog
        open={!!viewRow}
        onClose={() => setViewRow(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          Submission Details
        </DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 2, gap: 1 }}>
                <Chip label={`VoteId: ${viewRow.VoteId}`} />
                <Chip
                  label={`Email: ${viewRow.UserEmail || viewRow.InviteEmail || "N/A"}`}
                />
                <Chip label={`Member: ${viewRow.MemberCode || "N/A"}`} />
                <Chip label={`IP: ${viewRow.IpAddress || "N/A"}`} />
              </Stack>

              {(viewRow.answers || []).length === 0 ? (
                <Typography sx={{ color: "#6b778c" }}>
                  No answers stored for this vote.
                </Typography>
              ) : (
                (viewRow.answers || []).map((a, i) => {
                  const val = isTextQuestion(a.question_type)
                    ? a.AnswerText
                    : a.MultiOptionText ||
                      a.OptionText ||
                      a.AnswerText ||
                      "N/A";

                  return (
                    <Box key={i} sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                        {a.question_text}
                      </Typography>
                      <Typography sx={{ color: "#6b778c", mt: 0.5 }}>
                        {val || "N/A"}
                      </Typography>
                      <Divider sx={{ mt: 1.25 }} />
                    </Box>
                  );
                })
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setViewRow(null)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!drill}
        onClose={() => setDrill(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            boxShadow: "0 18px 60px rgba(0,0,0,0.16)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#223142" }}>
          {drill ? (
            <>
              Who voted for: <b>{drill.optionText}</b>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                {drill.questionText}
              </Typography>
            </>
          ) : null}
        </DialogTitle>

        <DialogContent dividers>
          {!drill ? null : drill.rows.length === 0 ? (
            <Typography sx={{ color: "#6b778c" }}>
              No matching voters found.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table
                size="small"
                sx={{
                  minWidth: 760,
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: "rgba(47,99,173,0.04)",
                    color: "#223142",
                    fontWeight: 700,
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                  },
                  "& .MuiTableBody-root .MuiTableRow-root:hover": {
                    backgroundColor: "rgba(47,99,173,0.025)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Member Code</TableCell>
                    <TableCell>Date Submitted</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drill.rows.map((r, idx) => (
                    <TableRow key={r.VoteId || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{r.FullName || "N/A"}</TableCell>
                      <TableCell>{r.UserEmail || r.InviteEmail || "N/A"}</TableCell>
                      <TableCell>{r.MemberCode || "N/A"}</TableCell>
                      <TableCell>
                        {r.SubmittedAt
                          ? new Date(r.SubmittedAt).toLocaleString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>{r.IpAddress || "N/A"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setViewRow(r)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "10px",
                            borderColor: "rgba(47,99,173,0.18)",
                            color: "#2f63ad",
                            "&:hover": {
                              borderColor: "#2f63ad",
                              backgroundColor: "rgba(47,99,173,0.04)",
                            },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDrill(null)}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResultsTab;
